import type { ItemId } from "./items.js";
import { ITEMS } from "./items.js";
import { RECIPES, recipeSkillId } from "./recipes.js";
import type { SkillId } from "./skills.js";

export interface GatherNodeType {
  itemId: ItemId;
  name: string;
  skillId: SkillId;
  reqLevel: number;
  xp: number;
  /** Base channel time in milliseconds (before level / tool / boost multipliers). */
  durationMs: number;
  /** How many times this spot can be harvested before it rests. */
  charges: number;
  respawnMs: number;
}

export const DIG_NODES: readonly GatherNodeType[] = [
  { itemId: "pebble", name: "Pebbles", skillId: "digging", reqLevel: 1, xp: 18, durationMs: 1400, charges: 4, respawnMs: 25000 },
  { itemId: "bone_shard", name: "Bones", skillId: "digging", reqLevel: 1, xp: 18, durationMs: 1400, charges: 4, respawnMs: 28000 },
  { itemId: "iron_nugget", name: "Iron", skillId: "digging", reqLevel: 10, xp: 35, durationMs: 1500, charges: 4, respawnMs: 32000 },
  { itemId: "charcoal", name: "Charcoal", skillId: "digging", reqLevel: 15, xp: 50, durationMs: 1600, charges: 4, respawnMs: 35000 },
  { itemId: "silver_tag", name: "Silver", skillId: "digging", reqLevel: 20, xp: 65, durationMs: 1700, charges: 3, respawnMs: 40000 },
  { itemId: "gold_medallion", name: "Gold", skillId: "digging", reqLevel: 40, xp: 78, durationMs: 1800, charges: 3, respawnMs: 45000 },
  { itemId: "diamond_chew", name: "Diamond", skillId: "digging", reqLevel: 70, xp: 250, durationMs: 3000, charges: 3, respawnMs: 120000 },
  { itemId: "nova_bone", name: "Nova", skillId: "digging", reqLevel: 80, xp: 320, durationMs: 3500, charges: 3, respawnMs: 110000 },
];

export const FETCH_NODES: readonly GatherNodeType[] = [
  { itemId: "twig", name: "Twigs", skillId: "fetching", reqLevel: 1, xp: 20, durationMs: 1300, charges: 4, respawnMs: 20000 },
  { itemId: "stick", name: "Sticks", skillId: "fetching", reqLevel: 15, xp: 45, durationMs: 1500, charges: 4, respawnMs: 28000 },
  { itemId: "hardwood_stick", name: "Hardwood", skillId: "fetching", reqLevel: 35, xp: 95, durationMs: 1800, charges: 3, respawnMs: 38000 },
  { itemId: "maple_branch", name: "Maple", skillId: "fetching", reqLevel: 60, xp: 190, durationMs: 2400, charges: 3, respawnMs: 55000 },
  { itemId: "enchanted_stick", name: "Magic Stick", skillId: "fetching", reqLevel: 80, xp: 280, durationMs: 3200, charges: 3, respawnMs: 120000 },
];

/** Fallen snacks dogs can sniff up - no tool required; speed scales with Sniffing level. */
export const FORAGE_NODES: readonly GatherNodeType[] = [
  { itemId: "fallen_crumb", name: "Crumbs", skillId: "sniffing", reqLevel: 1, xp: 16, durationMs: 1200, charges: 4, respawnMs: 18000 },
  { itemId: "windfall_berry", name: "Berries", skillId: "sniffing", reqLevel: 1, xp: 18, durationMs: 1300, charges: 4, respawnMs: 22000 },
  { itemId: "park_apple", name: "Apple", skillId: "sniffing", reqLevel: 10, xp: 32, durationMs: 1500, charges: 3, respawnMs: 28000 },
  { itemId: "cheese_rind", name: "Cheese", skillId: "sniffing", reqLevel: 20, xp: 55, durationMs: 1600, charges: 3, respawnMs: 34000 },
  { itemId: "picnic_scrap", name: "Scraps", skillId: "sniffing", reqLevel: 40, xp: 95, durationMs: 1900, charges: 3, respawnMs: 42000 },
  { itemId: "gourmet_nibble", name: "Nibble", skillId: "sniffing", reqLevel: 70, xp: 220, durationMs: 2600, charges: 3, respawnMs: 90000 },
  { itemId: "royal_truffle", name: "Truffle", skillId: "sniffing", reqLevel: 85, xp: 310, durationMs: 3200, charges: 2, respawnMs: 120000 },
];

/**
 * Park people on benches / cookouts - whimper / posture for handouts.
 * Labels are cozy park regulars (same voice as Barksmith Bo / Parent Pam).
 * No tool; channel speed scales with Begging level. Respawns = human cools off.
 */
export const BEG_NODES: readonly GatherNodeType[] = [
  { itemId: "handout_crumb", name: "Bench Bea", skillId: "begging", reqLevel: 1, xp: 20, durationMs: 1800, charges: 3, respawnMs: 28000 },
  { itemId: "soft_biscuit", name: "Lawn Lou", skillId: "begging", reqLevel: 1, xp: 22, durationMs: 1900, charges: 3, respawnMs: 30000 },
  { itemId: "pretzel_piece", name: "Visitor Vera", skillId: "begging", reqLevel: 10, xp: 40, durationMs: 2100, charges: 3, respawnMs: 36000 },
  { itemId: "cafe_scone", name: "Stroll Sid", skillId: "begging", reqLevel: 25, xp: 70, durationMs: 2300, charges: 2, respawnMs: 42000 },
  { itemId: "park_hotdog", name: "Cheer Fran", skillId: "begging", reqLevel: 40, xp: 110, durationMs: 2600, charges: 2, respawnMs: 50000 },
  { itemId: "fancy_croissant", name: "Blanket Bill", skillId: "begging", reqLevel: 65, xp: 200, durationMs: 3000, charges: 2, respawnMs: 75000 },
  { itemId: "steak_tip", name: "Proud Pat", skillId: "begging", reqLevel: 85, xp: 300, durationMs: 3400, charges: 2, respawnMs: 110000 },
];

export const GATHER_BY_ITEM: Record<string, GatherNodeType> = Object.fromEntries(
  [...DIG_NODES, ...FETCH_NODES, ...FORAGE_NODES, ...BEG_NODES].map((node) => [node.itemId, node]),
);

export function gatherType(itemId: string): GatherNodeType | undefined {
  return GATHER_BY_ITEM[itemId];
}

const CRAFT_OUTPUT_SKILL: Partial<Record<ItemId, SkillId>> = Object.fromEntries(
  RECIPES.map((recipe) => [recipe.output, recipeSkillId(recipe)]),
);

/** Which skill an item is associated with (harvest, craft, or boost). */
export function itemSkillId(itemId: ItemId): SkillId | undefined {
  const gather = GATHER_BY_ITEM[itemId];
  if (gather) {
    return gather.skillId;
  }
  const crafted = CRAFT_OUTPUT_SKILL[itemId];
  if (crafted) {
    return crafted;
  }
  const def = ITEMS[itemId];
  if (def?.boostSkill) {
    return def.boostSkill;
  }
  if (def?.toolKind) {
    return "toycraft";
  }
  return undefined;
}
